import { useTranslation } from 'react-i18next';
import { Mic, Paperclip } from 'lucide-react';
import { listTime } from './dayText';
import { avatarHue, initialsOf } from './threadModel';

export interface ChatListRowProps {
  /** Whose disc: the counterpart in a DM, the last sender in a group thread. */
  avatarId: string;
  avatarName: string;
  /** The person's name for a DM; "#42 · Room 302" for a ticket thread. */
  title: string;
  /** The last message, or the empty-thread text. */
  preview: string;
  /** "Ana" in a group thread — the sender's first name. Omitted in a 1:1. */
  previewPrefix?: string;
  /** A glyph in place of a body when the last message was media. */
  media?: 'photo' | 'voice' | null;
  /** When the last message landed; null for a thread nobody has spoken in. */
  at: string | null;
  now: Date;
  onOpen: () => void;
}

/**
 * One row of the chat list, in the reference's grammar: disc, name, preview, time.
 *
 * No unread badge, deliberately. Read state does not exist on either message model
 * (DEBT-22), and a badge that counts nothing is a lie a technician learns to ignore.
 * The right-hand column reserves the space under the time so the badge slots in later
 * without a re-layout — an empty slot today, never a fake number.
 */
export function ChatListRow({
  avatarId,
  avatarName,
  title,
  preview,
  previewPrefix,
  media = null,
  at,
  now,
  onOpen,
}: ChatListRowProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-[64px] w-full items-center gap-3 bg-transparent px-3 py-2 text-left"
    >
      <InitialsAvatar id={avatarId} name={avatarName} />

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-100">{title}</span>
        <span className="flex items-center gap-1 truncate text-xs text-slate-400">
          {previewPrefix && <span className="shrink-0 text-slate-300">{previewPrefix}:</span>}
          {media === 'photo' && <Paperclip className="size-3 shrink-0" aria-hidden="true" />}
          {media === 'voice' && <Mic className="size-3 shrink-0" aria-hidden="true" />}
          <span className="truncate">
            {media === 'photo' ? t('chat.photo') : media === 'voice' ? t('chat.voiceNote') : preview}
          </span>
        </span>
      </span>

      <span className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-[11px] tabular-nums text-slate-500">
          {at ? <time dateTime={at}>{listTime(at, now, t)}</time> : ''}
        </span>
        {/* Reserved for the unread count once read state exists (DEBT-22). Empty on purpose. */}
        <span aria-hidden="true" data-slot="unread" className="size-5" />
      </span>
    </button>
  );
}

/**
 * Initials on a disc whose colour comes from the user id, so a person is the same colour
 * on every screen. Initials, never a photo: there are no staff photos, collecting them is a
 * privacy conversation nobody needs, and the disc does the same recognition job.
 */
export function InitialsAvatar({ id, name }: { id: string; name: string }) {
  return (
    <span
      aria-hidden="true"
      data-hue={avatarHue(id)}
      className={`flex size-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${avatarHue(id)}`}
    >
      {initialsOf(name)}
    </span>
  );
}
